import { Box, Button, Typography, useMediaQuery, useTheme } from '@mui/material';
import CustomRating from '../CustomRating/CustomRating';
import React, { FC, useContext, useState } from 'react';
import SurveyService from '../../services/SurveyService';
import NegativeFeedback from './NegativeFeedback';
import PositiveFeedback from './PositiveFeedback';
import { useTranslation } from 'react-i18next';
import { LocalTranslationContext } from '../../hooks/useLocalTranslation';

interface Props {
  companyLogo: string;
  primaryColor: string;
  secondaryColor: string;
  question: string;
  companyWebsiteUrl: string;
  customFont: string;
  id: string;
  score: number;
  imprintLink: string;
  privacyPolicyLink: string;
  companyName: string;
  platform: string;
  platformUrl: string;
  platformName: string;
  contactEmail: string;
  title?: string;
  outroText?: string;
  introText?: string;
  contactForm?: boolean;
  scale?: {
    highText: string;
    lowText: string;
  };
  feedbackText?: string;
  feedbackTitle?: string;
  feedbackActive?: boolean;
}

const Feedback: FC<Props> = (props) => {
  const {
    companyLogo,
    primaryColor,
    secondaryColor,
    question,
    companyWebsiteUrl,
    customFont,
    id,
    score,
    imprintLink,
    privacyPolicyLink,
    companyName,
    platform,
    platformUrl,
    platformName,
    contactEmail,
    title,
    outroText,
    contactForm,
    scale,
    introText,
    feedbackTitle,
    feedbackText,
    feedbackActive,
  } = props;

  const theme = useTheme();
  const localContext = useContext(LocalTranslationContext);
  const { t: globalT } = useTranslation();
  const t = localContext ? localContext.t : globalT;
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));

  const [showButton, setShowButton] = useState<boolean>(true);
  const [selectedRating, setSelectedRating] = useState<number>(score);
  const [feedbackType, setFeedbackType] = useState('default');
  const [lastAnswerId, setLastAnswerId] = useState<string>('');
  const [feedback, setFeedback] = useState<string>('');

  const isNegativeRating = (selectedRating: number) => {
    return selectedRating < score;
  };

  const handleClick = async () => {
    setFeedbackType(isNegativeRating(selectedRating) ? 'negative' : 'positive');

    try {
      const answer = await SurveyService.saveNpsSurveyAnswer(
        id,
        selectedRating,
        contactEmail,
        formatTextToString(feedback),
        companyName
      );
      setLastAnswerId(answer.ID);
    } catch (error) {
      console.error('Ein Fehler ist beim Speichern der Antwort aufgetreten:', error);
    }

    setShowButton(false);
  };

  const handleRatingClick = (rating: number) => {
    setSelectedRating(rating);
  };

  const formatTextToString = (text: any) => {
    if (!text) return null;

    return text.replace(/(\r\n|\r|\n)/g, ' ');
  };

  return (
    <Box
      sx={{
        backgroundColor: '#ffffff',
        width: 'auto',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        alignSelf: 'center',
        ml: isSmallScreen ? '1%' : '10%',
        mr: isSmallScreen ? '1%' : '10%',
        mt: 'auto',
      }}
    >
      {feedbackType === 'default' && (
        <Box>
          <Typography
            variant="h4"
            sx={{
              display: 'flex',
              flexDirection: 'row',
              justifyContent: 'center',
              color: secondaryColor,
              mb: theme.spacing(6),
              textAlign: isSmallScreen ? 'center' : 'left',
              fontWeight: isSmallScreen ? 'normal' : 'bold',
              fontFamily: customFont,
            }}
          >
            {question}
          </Typography>
          <CustomRating
            highText={scale?.highText}
            lowText={scale?.lowText}
            min={0}
            max={10}
            font={customFont}
            isSmallScreen={isSmallScreen}
            onRatingClick={handleRatingClick}
            currentRating={selectedRating}
          />
          {showButton && (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'row',
                justifyContent: 'end',
                alignItems: 'center',
                width: '100%',
                mt: theme.spacing(4),
              }}
            >
              <Button
                variant="contained"
                value={feedbackActive ? 'next' : 'send'}
                sx={{
                  backgroundColor: primaryColor,
                  width: 'fit-content',
                  padding: isSmallScreen ? theme.spacing(1) : theme.spacing(2),
                  marginTop: theme.spacing(4),
                }}
                onClick={() => handleClick()}
              >
                {!feedbackActive || !isNegativeRating(selectedRating) ? t('send') : t('next')}
              </Button>
            </Box>
          )}
        </Box>
      )}

      {feedbackType === 'negative' && lastAnswerId && (
        <NegativeFeedback
          imprint={imprintLink}
          dataProtection={privacyPolicyLink}
          companyName={companyName}
          secondaryColor={secondaryColor}
          selectedRating={selectedRating}
          companyLogo={companyLogo}
          primaryColor={primaryColor}
          platform={platform}
          contactEmail={contactEmail}
          companyWebsiteUrl={companyWebsiteUrl}
          customFont={customFont}
          title={title}
          outroText={outroText}
          introText={introText}
          contactForm={contactForm}
          id={id}
          onChange={setFeedback}
          feedbackText={feedbackText}
          feedbackTitle={feedbackTitle}
          feedbackActive={feedbackActive}
          lastAnswerId={lastAnswerId}
        />
      )}

      {feedbackType === 'positive' && (
        <PositiveFeedback
          companyWebsiteUrl={companyWebsiteUrl}
          secondary={secondaryColor}
          companyLogo={companyLogo}
          primary={primaryColor}
          platform={platform}
          platformUrl={platformUrl}
          platformName={platformName}
          customFont={customFont}
          title={title}
          outroText={outroText}
          companyName={companyName}
          feedbackActive={feedbackActive}
        />
      )}
    </Box>
  );
};

export default Feedback;
